/**
 * Products Domain Module
 *
 * Handles all product-related operations including:
 * - Creating, updating, and deleting products
 * - Product images and stock management
 * - Product movement tracking
 * - Categories, suppliers, and units
 *
 * This module provides proper TypeScript types for product operations.
 */

import { Platform } from 'react-native'
import { format } from 'date-fns'
import { getDefaultStore } from 'jotai'
import * as Sentry from '@sentry/react-native'

// Firebase primitives (from firebase.ts to avoid circular deps)
import { rootDb, fieldValue, storage } from './firebase'
import { sortMovementsForChain } from './stockBaseline'

// Shared types and utilities
import {
  shouldUseSupabase,
  type DateRange,
  type TimestampLike,
  type MockRef,
} from './types'
import { dualWriteUpdate } from './updates'
import { bestEffortDualWrite, bestEffortDualWriteDelete } from './dualWrite'
import { sendBulkStockMirror } from './stockMirror'
import { buildWriteBackDelta } from './writeBackDelta'
import { getFirestoreRef, type ItemWithRef } from './firestoreRefUtils'
import { ingredientQuantityToNumber, parseDecimalValue } from '../../utils/ingredientQuantity'
// Cycle-safe product sanitizer (ATL-554) — extracted so it's unit-testable in isolation
import { sanitizeForFirestore } from './productSanitize'

import {
  productsDictAtom,
  productsUpdatedAtom,
  serverProductsDictAtom,
  storesDictAtom,
} from '../AtomsProvider'

import { getLocalStoreDate } from '../../utils/timezoneFunctions'
import { productsApi, auditLogApi, productMovementsApi } from '../API/typedApiClient'
import { readWholeList, newestFirst } from '../API/readWholeList'
import { reportFirestoreOnlyWriterUsage } from '../analytics/firestoreOnlyWriterUsage'
import { assertWWriterAvailable } from '../Write/writeW'
import { commitGuardedCreate } from './writeClaims'

// ============================================================================
// Types
// ============================================================================

/**
 * Product ingredient for composite products
 */
export interface ProductIngredient {
  id: string
  name: string
  quantity: number
  unit?: string
  ref?: MockRef
}

/**
 * Product price for a specific store
 */
export interface StorePrice {
  storeId: string
  price: number
  priceType?: string
}

/**
 * Product category
 */
export interface ProductCategory {
  id: string
  name: string
  color?: string
  icon?: string
  parentId?: string
  organizationId?: string
}

/**
 * Product supplier
 */
export interface ProductSupplier {
  id: string
  name: string
  phone?: string
  email?: string
  address?: string
  organizationId?: string
}

/**
 * Complete product information
 */
export interface Product {
  id?: string
  ref?: MockRef
  organizationId: string
  name: string
  description?: string
  sku?: string
  barcode?: string

  // Category and supplier
  categoryId?: string
  category?: ProductCategory
  supplierId?: string
  supplier?: ProductSupplier

  // Pricing
  purchasePrice?: number
  packagePrice?: number
  salePrices?: Record<string, number>
  storePrices?: Record<string, number | { price: number; priceType?: string }>
  prices?: StorePrice[]

  // Stock
  trackInventory?: boolean
  storeStock?: Record<string, number>
  unit?: string

  // Product type
  productType?: 'product' | 'service' | 'composite'
  type?: string
  isService?: boolean
  isComposite?: boolean
  manufacturedBeforeSale?: boolean
  ingredients?: ProductIngredient[]

  // Display settings
  isHiddenInNewSale?: boolean
  activeInStore?: Record<string, boolean>
  image?: string

  // Audit fields
  lastChangeAt?: Date | TimestampLike
  lastChangeStore?: string
  lastChangeBy?: string
  dailyStatID?: string
  createdAt?: Date | TimestampLike
  updatedAt?: Date | TimestampLike
}

/**
 * Product movement record
 */
export interface ProductMovement {
  id?: string
  ref?: MockRef
  productID: string
  type:
    | 'sale' | 'supply' | 'transfer' | 'revision' | 'manufacture' | 'return'
    | 'writeOff' | 'newProduct' | 'manual'
    | 'complexSale' | 'editComplexSale'
    | 'deleteSale' | 'deleteSupply' | 'deleteWriteOff' | 'deleteManufacture'
    | 'deleteRevision' | 'deleteReturn' | 'deleteComplexSale'
  previousInStock: number
  /**
   * Index of this row within the ONE operation that wrote it (ATL-1381) — the only deterministic
   * tiebreak for rows sharing a commit timestamp. Absent on every movement written before the
   * field existed, which `sortMovementsForChain` treats as 0.
   */
  writeSeq?: number
  increment: number
  date: Date | TimestampLike
  linkedDocId?: string
  linkedDocDate?: Date | TimestampLike
  clientSaveDate?: Date | TimestampLike
  store: string
  organizationId: string
  comment?: string
}

/**
 * Product movement grouped by date
 */
export interface MovementsByDate {
  title: string
  data: ProductMovement[]
}

/**
 * Parameters for creating a product
 *
 * Uses Firestore field names (categoryID, supplierID, productUnit, type, salePrices)
 * which match the output of productValidation.js
 */
export interface CreateProductParams {
  organizationId: string
  name: string
  description?: string
  sku?: string
  barcode?: string
  categoryID?: string
  supplierID?: string
  purchasePrice?: number
  packagePrice?: number
  trackInventory?: boolean
  productUnit?: string
  type?: boolean  // true = weighted/fractional (sold by weight)
  isService?: boolean  // true = service (no physical inventory)
  isHiddenInNewSale?: boolean
  isComposite?: boolean
  manufacturedBeforeSale?: boolean
  ingredients?: ProductIngredient[]
  activeInStore?: Record<string, boolean>
  salePrices?: Record<string, number>
  storeStock?: Record<string, number>
  // ATL-868: declared so the dual-write payload can carry them. productValidation.js has always
  // emitted all three into the Firestore doc; the Supabase payload just never listed them.
  importance?: number
  stockThreshold?: number
  productImages?: Array<Record<string, unknown>>
  // ATL-1772: likewise emitted by productValidation.js and, until then, never sent.
  defaultQuantity?: number
  barcodes?: string[]
  punchCardEnabled?: boolean
  activeInEcommerce?: boolean
  oldPrice?: number
  priceTypes?: Record<string, unknown>
  priceTypeScopes?: Record<string, unknown>
  lastChangeAt: Date
  lastChangeStore: string
  dailyStatID: string
}

/**
 * Parameters for updating a product
 */
export interface UpdateProductParams {
  name?: string
  description?: string
  sku?: string
  barcode?: string
  categoryId?: string
  purchasePrice?: number
  packagePrice?: number
  trackInventory?: boolean
  unit?: string
  productType?: 'product' | 'service' | 'composite'
  isHiddenInNewSale?: boolean
  isComposite?: boolean
  manufacturedBeforeSale?: boolean
  ingredients?: ProductIngredient[]
  activeInStore?: Record<string, boolean>
  storePrices?: Record<string, number>
  lastChangeAt: Date
  lastChangeStore: string
  dailyStatID: string
  organizationId: string
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Group movements by date for display
 */
const groupMovementsByDate = (movements: ProductMovement[], storeId: string): MovementsByDate[] => {
  const grouped: Record<string, ProductMovement[]> = {}

  movements.forEach((movement) => {
    const movementDate = movement.date && typeof (movement.date as TimestampLike).toDate === 'function'
      ? (movement.date as TimestampLike).toDate()
      : new Date(movement.date as string | Date)
    const dateKey = format(getLocalStoreDate(storeId, movementDate), 'd/M/y EEEE')

    if (!grouped[dateKey]) {
      grouped[dateKey] = []
    }
    grouped[dateKey].push(movement)
  })

  return Object.entries(grouped).map(([title, data]) => ({ title, data }))
}

// ============================================================================
// CRUD Operations
// ============================================================================

/**
 * Create a new product
 */
export const saveNewProduct = async (
  db: unknown,
  productInfo: CreateProductParams
): Promise<MockRef> => {
  assertWWriterAvailable('global/Firebase/products.ts#saveNewProduct')
  const batch = rootDb.batch()
  // Firestore refs have collection() method - use unknown to bypass strict typing
  const dbRef = db as unknown as { collection: (name: string) => { doc: () => MockRef } }
  const ref = dbRef.collection('products').doc()

  // Sanitize data for Firestore - converts mock Timestamps to Dates
  const sanitizedProductInfo = sanitizeForFirestore(productInfo) as Record<string, unknown>

  batch.set(ref, sanitizedProductInfo)

  // Create history entry - Firestore refs have subcollection access
  const refWithCollection = ref as unknown as { collection: (name: string) => { doc: () => MockRef } }
  const historyRef = refWithCollection.collection('history').doc()
  batch.set(historyRef, sanitizedProductInfo)

  // Create movement entries for initial stock
  if (productInfo.storeStock) {
    for (const storeId in productInfo.storeStock) {
      const movementRef = refWithCollection.collection('movement').doc()
      batch.set(movementRef, {
        productID: ref.id,
        type: 'newProduct',
        previousInStock: 0,
        increment: productInfo.storeStock[storeId],
        date: fieldValue.serverTimestamp(),
        linkedDocId: ref.id,
        linkedDocDate: productInfo.lastChangeAt,
        clientSaveDate: new Date(),
        store: storeId,
        organizationId: productInfo.organizationId,
      })
    }
  }

  // Update daily stats
  const statFields = {
    productsCreated: fieldValue.increment(1),
  }
  batch.set(
    (db as { collection: (name: string) => { doc: (id: string) => unknown } }).collection('dailyStats').doc(productInfo.dailyStatID),
    {
      ...statFields,
      store: productInfo.lastChangeStore,
      date: productInfo.lastChangeAt,
      organizationId: productInfo.organizationId,
    },
    { merge: true }
  )

  await commitGuardedCreate('products.create', db, ref, batch) // ATL-2004: a re-delivery cannot apply twice

  // Dual write to Supabase via NestJS backend
  // productInfo comes from productValidation.js which already outputs Firestore format
  // (categoryID, supplierID, productUnit, type boolean, salePrices as Record<string, number>)
  // Just pass through the fields - no transformation required
  const productDto = {
    firestoreId: ref.id,
    organizationId: productInfo.organizationId,
    name: productInfo.name,
    description: productInfo.description || '',
    sku: productInfo.sku || '',
    barcode: productInfo.barcode ? String(productInfo.barcode) : '',
    categoryID: productInfo.categoryID,
    supplierID: productInfo.supplierID,
    trackInventory: productInfo.trackInventory,
    productUnit: productInfo.productUnit,
    purchasePrice: productInfo.purchasePrice || 0,
    packagePrice: productInfo.packagePrice || 0,
    isHiddenInNewSale: productInfo.isHiddenInNewSale || false,
    type: productInfo.type, // weighted/fractional (sold by weight)
    isService: productInfo.isService || false, // service (no physical inventory)
    isComposite: productInfo.isComposite || false,
    manufacturedBeforeSale: productInfo.manufacturedBeforeSale || false,
    // Map ingredients to required fields only (id, name, quantity)
    ingredients: productInfo.ingredients?.map(ing => ({
      id: ing.id,
      name: ing.name,
      // Belt-and-suspenders: backend IngredientDto.quantity is @IsNumber(), but the editor can
      // leave quantity as a (possibly comma-decimal) string (ATL-633). Coerce comma-aware so the
      // dual-write payload matches the Firestore value — "1,5" → 1.5, not 1 or 0.
      quantity: ingredientQuantityToNumber(ing.quantity),
    })),
    activeInStore: productInfo.activeInStore,
    salePrices: productInfo.salePrices,
    storeStock: productInfo.storeStock,
    // ATL-868: the same instant Firestore just committed. products.update already carries this
    // (its DTO is a pass-through of the whole doc); create built an explicit payload and left it
    // out, so a product created through the dual-write had no `last_change_at` in Supabase at all
    // — 1,823 rows across 229 orgs on 2026-08-06. Sent as an ISO string; the backend refuses
    // anything that is not genuinely an instant rather than substituting its own clock.
    lastChangeAt: productInfo.lastChangeAt,
    // ATL-868: three more fields the explicit create payload silently omitted while the
    // pass-through UPDATE payload carried them — so a product got the Supabase COLUMN DEFAULT
    // until somebody happened to edit it. Fleet census 2026-08-06 over all 41,038 Firestore
    // product docs:
    //   importance      1,009 products / 40 orgs flattened to the column default 1
    //   stockThreshold    258 products / 35 orgs, likewise (column default 1, and the reorder
    //                     threshold is what drives the low-stock alert)
    //   productImages     337 products / 13 orgs reached Supabase with no image at all — the
    //                     backend DTO already declared the field, nothing read it (now fixed).
    importance: productInfo.importance,
    stockThreshold: productInfo.stockThreshold,
    productImages: productInfo.productImages,
    // ATL-1772: the rest of the fields productValidation.js writes into the new Firestore doc and
    // this explicit payload still omitted — the same ATL-868 shape, missed by it. The backend has
    // handled every one on create since ATL-447 (`directCreate`; priceTypes via the RPC), so the
    // only thing losing them was this list: a product created on the till kept the Supabase COLUMN
    // DEFAULT until somebody edited it. Measured 2026-10-22: UNDERWOOD DECOR's compare-spotcheck
    // blocker is a product created 2026-10-06 with `defaultQuantity: 0` in Firestore and the column
    // default 1 in Supabase — at cutover its till pre-fills 1 instead of 0. `oldPrice` is only on
    // productInfo when it is a finite number (ATL-880), so absent stays absent.
    // (`productTags` has no Supabase column — reverse-mirror product.builder.ts lists it degraded.)
    defaultQuantity: productInfo.defaultQuantity,
    barcodes: productInfo.barcodes,
    punchCardEnabled: productInfo.punchCardEnabled,
    activeInEcommerce: productInfo.activeInEcommerce,
    oldPrice: productInfo.oldPrice,
    priceTypes: productInfo.priceTypes,
    priceTypeScopes: productInfo.priceTypeScopes,
  }

  const response = await bestEffortDualWrite('products.create', () => productsApi.createProduct(productDto))
  const productId = response?.id || ref.id

  // Seed initial stock — ONE bulk call for all stores (ATL-765; label and movement shape are the
  // same as the per-store loop this replaces). Values are coerced comma-aware (ATL-633): the old
  // unvalidated single-item endpoint let a string travel into the RPC cast, but the bulk DTO is
  // @IsNumber() and the client re-validates with Zod, so an uncoerced "1,5" would now be rejected
  // client-side; an unparseable value is skipped rather than sent as 0 (never zero real stock).
  if (productInfo.storeStock) {
    const initialStock: Array<{ storeId: string; newStock: number }> = []
    for (const storeId of Object.keys(productInfo.storeStock)) {
      const stockValue = productInfo.storeStock[storeId]
      if (stockValue === undefined || stockValue === null) continue
      const newStock = parseDecimalValue(stockValue)
      if (newStock === null) continue
      initialStock.push({ storeId, newStock })
    }
    // foldSource null: a freshly minted product id cannot have queued outbox entries.
    await sendBulkStockMirror(productId, initialStock, null)
  }

  // Note: Product history is tracked automatically by the backend via stored procedures

  // Update Jotai atoms so ProductList re-renders immediately. ATL-1445 stage 3 P6: the SERVER atom,
  // read and written — `productsDictAtom` is derived (server + this device's unsent writes) and
  // read-only, and merging into it would bake the local overlay into the server copy.
  const jotaiStore = getDefaultStore()
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- productsDictAtom has complex union type
  const currentProductsDict = jotaiStore.get(serverProductsDictAtom) as Record<string, any>

  // Build the new product object for the atom (using Firestore format)
  // Put id at the end to ensure it overwrites any id from spreads
  const newProduct = {
    ...productInfo,
    // Ensure the response data is merged if available
    ...(response || {}),
    id: ref.id, // Use Firestore ID as the main ID (must be last to override)
  }

  // Use Firestore ID as the key (same as updateProduct and subscriptionFactory)
  const productKey = ref.id

  jotaiStore.set(serverProductsDictAtom, {
    ...currentProductsDict,
    [productKey]: newProduct,
  })

  // Trigger ProductList re-render by incrementing updateCount
  const currentUpdated = jotaiStore.get(productsUpdatedAtom)
  jotaiStore.set(productsUpdatedAtom, {
    ...currentUpdated,
    updateCount: currentUpdated.updateCount + 1,
    lastLocalUpdate: new Date(),
  })

  return ref
}

/**
 * Delete a product
 *
 * `refOrItem` accepts the whole product object (preferred — works even when `.ref` was
 * stripped, e.g. items hydrated without a live Firestore ref; QARASAY-KASSA-26K) or a
 * ref/mock ref. The Firestore id is taken from the resolved ref, never from `.ref.id`.
 */
export const deleteProduct = async (
  db: unknown,
  refOrItem: MockRef | ItemWithRef,
  product: Product,
  uid: string
): Promise<void> => {
  assertWWriterAvailable('global/Firebase/products.ts#deleteProduct')
  const batch = rootDb.batch()
  // Fall back to the product itself so a missing `.ref` on the passed item can't throw
  const firestoreRef = getFirestoreRef(refOrItem ?? product, 'products')
  const productId: string = firestoreRef.id

  batch.delete(firestoreRef)

  // Write to updates for audit trail
  //
  // ATL-915: this used to be `firestoreRef.collection('updates')`, i.e. a SUBCOLLECTION OF THE
  // PRODUCT BEING DELETED — `organizations/{org}/products/{productId}/updates/{autoId}` — while
  // `batch.delete(firestoreRef)` above removed the parent in the same commit. Firestore keeps a
  // deleted document's subcollections, so the audit entry survived somewhere nothing reads it: the
  // Updates screen lists `organizations/{org}/updates`, and so does the migration set-diff. Two
  // consequences, both live on prod until this line changed:
  //
  //   • deleting a product produced NO visible audit entry in Firestore, unlike the other three
  //     changelog writers (deleteSale, deleteWorkingHour, deleteComplexSale), which all use `db`;
  //   • the Supabase mirror's `firestore_id` named a document the set-diff could not find, so all
  //     1,228 such rows classified as prunable orphans (see PRUNE_FORBIDDEN_COLLECTIONS).
  //
  // `db` is the organization document ref — the same one every other call site writes through.
  const orgRef = db as { collection: (name: string) => { doc: () => unknown } }
  // ATL-911: bind the ref BEFORE building the record, so the document's generated id is
  // part of it. An anonymous `.doc()` mints an id and discards it, which is why the
  // Supabase mirror landed with firestore_id NULL and its Firestore twin read as missing.
  const deleteProductUpdateRef = orgRef.collection('updates').doc()
  const deleteProductUpdate = {
    firestoreId: (deleteProductUpdateRef as { id: string }).id,
    date: new Date(),
    cashierUID: uid,
    type: 'deleteProduct',
    dataBefore: sanitizeForFirestore(product),
  }
  batch.set(deleteProductUpdateRef, deleteProductUpdate)

  await batch.commit()

  // Dual write to Supabase
  await bestEffortDualWriteDelete('products.delete', () => productsApi.deleteProduct(productId), { firestoreId: productId })

  // Mirror the audit-log entry to Supabase (best-effort)
  await dualWriteUpdate(deleteProductUpdate)

  // Write to audit_log (using unknown cast for API compatibility)
  await bestEffortDualWrite('products.auditLog.create', () => auditLogApi.createAuditLog({
    tableName: 'products',
    recordId: productId,
    operation: 'DELETE',
    // Raw product is safe here (unlike dataBefore above): the typed client's
    // sanitizeRequestBody strips live refs/cycles at the HTTP boundary (ATL-523)
    oldData: product as unknown as Record<string, unknown>,
    userId: uid,
  } as unknown as Parameters<typeof auditLogApi.createAuditLog>[0]))
}

// ============================================================================
// Image Operations
// ============================================================================

/**
 * Upload a product image to Firebase Storage
 */
export const uploadProductImage = async (
  file: Blob | string,
  name: string
): Promise<unknown> => {
  reportFirestoreOnlyWriterUsage('uploadProductImage')
  if (Platform.OS === 'web') {
    return await storage.child('productImages/' + name).put(file as Blob, {
      contentType: 'image/jpeg',
    })
  } else {
    return await (storage.child('productImages/' + name) as { putFile: (file: string, options: { contentType: string }) => Promise<unknown> }).putFile(file as string, {
      contentType: 'image/jpeg',
    })
  }
}

/**
 * Delete a product image from Firebase Storage.
 *
 * Idempotent: if the object is already gone (or `name` is empty/stale), the
 * desired end state — no image at that path — is already satisfied, so we
 * resolve instead of throwing. Previously a `storage/object-not-found` rejection
 * propagated as an unhandled promise rejection from the image-remove button and
 * crashed the app, surfacing to users as "can't delete the product" (ATL-559).
 */
export const deleteProductImage = async (name: string): Promise<void> => {
  if (!name) return
  reportFirestoreOnlyWriterUsage('deleteProductImage')
  try {
    await storage.child('productImages/' + name).delete()
  } catch (error) {
    if ((error as { code?: string })?.code === 'storage/object-not-found') {
      // Already deleted — nothing to do.
      return
    }
    throw error
  }
}

// ============================================================================
// Movement Queries
// ============================================================================

/**
 * Get product movements within a date range
 */
export const getProductMovementOnDateRange = async (
  db: unknown,
  productID: string,
  range: DateRange,
  flat = false,
  store: string
): Promise<ProductMovement[] | MovementsByDate[]> => {
  if (shouldUseSupabase()) {
    try {
      const params = {
        productId: productID,
        storeId: store,
        startDate: range.startDate instanceof Date ? range.startDate.toISOString() : String(range.startDate),
        endDate: range.endDate instanceof Date ? range.endDate.toISOString() : String(range.endDate),
      }

      // Backend returns Firestore-compatible fields (productID, store) with dual IDs.
      // TypedApiClient auto-converts date/linkedDocDate/clientSaveDate to TimestampLike.
      // Frontend pass-through: no field mapping needed.
      // ATL-1477: every movement in the range — GET /product-movements returns 100 by default, and
      // a movement chain built from the newest 100 misstates every running balance below them.
      const movements = await readWholeList(
        (window) => productMovementsApi.getProductMovementsWindow({ ...params, ...window }),
        { label: 'product-movements', order: newestFirst('date') },
      ) as unknown as ProductMovement[]

      const ordered = sortMovementsForChain(movements)
      if (flat) {
        return ordered
      } else {
        return groupMovementsByDate(ordered, store)
      }
    } catch (error) {
      console.error('Error fetching product movements from Supabase:', error)
      Sentry.captureException(error, {
        tags: { supabase_fetch_error: true, collection: 'product-movements' },
        extra: { productID, range, store, errorMessage: (error as Error).message },
      })
      throw error
    }
  }

  // Firestore implementation - complex query chain requires type assertions
  type FirestoreQuery = {
    where: (field: string, op: string, value: unknown) => FirestoreQuery
    orderBy: (field: string, direction: string) => FirestoreQuery
    get: () => Promise<{ forEach: (fn: (doc: { id: string; ref: MockRef; data: () => ProductMovement }) => void) => void }>
  }

  const productRef = (db as unknown as { collection: (name: string) => { doc: (id: string) => { collection: (name: string) => FirestoreQuery } } })
    .collection('products')
    .doc(productID)
    .collection('movement')

  let query: FirestoreQuery = productRef
  if (store) {
    query = query.where('store', '==', store)
  }

  const querySnapshot = await query
    .where('date', '>=', range.startDate)
    .where('date', '<=', range.endDate)
    .orderBy('date', 'desc')
    .get()

  const movements: ProductMovement[] = []
  querySnapshot.forEach((doc: { id: string; ref: MockRef; data: () => ProductMovement }) => {
    const movement = doc.data()
    movement.id = doc.id
    movement.ref = doc.ref
    movements.push(movement)
  })

  const ordered = sortMovementsForChain(movements)
  if (flat) {
    return ordered
  } else {
    return groupMovementsByDate(ordered, store)
  }
}

/**
 * Update stock manually for a product
 */
export const updateStockManually = async (
  db: unknown,
  productRef: MockRef,
  storeId: string,
  newStock: number,
  previousStock: number,
  comment: string,
  organizationId: string
): Promise<void> => {
  assertWWriterAvailable('global/Firebase/products.ts#updateStockManually')
  const batch = rootDb.batch()
  const storeStockField = `storeStock.${storeId}`
  const increment = newStock - previousStock

  batch.update(productRef, { [storeStockField]: newStock })

  // Create movement record - Firestore refs have subcollection access
  const movementRef = (productRef as unknown as { collection: (name: string) => { doc: () => MockRef } }).collection('movement').doc()
  batch.set(movementRef, {
    productID: productRef.id,
    type: 'manual',
    previousInStock: previousStock,
    increment,
    date: fieldValue.serverTimestamp(),
    linkedDocId: productRef.id,
    linkedDocDate: fieldValue.serverTimestamp(),
    clientSaveDate: new Date(),
    store: storeId,
    organizationId,
    comment,
  })

  await batch.commit()

  // Dual write to Supabase — through the SHARED bulk mirror (stockMirror.ts), like every other
  // producer of the products.updateStock label: a lone single-store call would supersede a
  // queued multi-store bulk entry for this product on failure, destroying the other stores'
  // retries (the (label, productId) outbox key). `comment` was only ever forwarded as `reason`,
  // which the backend has always ignored (updateStockManually's `_reason`); the movement doc
  // above keeps it. foldSource is the products dict's current map (the live view of the read
  // source) with this store's new absolute applied, so queued OTHER-store retries fold in with
  // their current values instead of being orphaned by this call's supersede/purge.
  const dictStoreStock = ((): Record<string, unknown> => {
    try {
      const dict = getDefaultStore().get(productsDictAtom) as
        | Record<string, { storeStock?: unknown }>
        | undefined
      const fromDict = dict?.[productRef.id]?.storeStock
      return fromDict && typeof fromDict === 'object'
        ? { ...(fromDict as Record<string, unknown>) }
        : {}
    } catch {
      return {}
    }
  })()
  await sendBulkStockMirror(productRef.id, [{ storeId, newStock }], {
    ...dictStoreStock,
    [storeId]: newStock,
  })
}

// ============================================================================
// Batch Operations
// ============================================================================

/**
 * Batch update multiple products
 */
export const batchUpdateProducts = async (
  db: unknown,
  products: Array<{ ref: MockRef; firestoreId?: string; id: string } & Partial<Product>>
): Promise<void> => {
  assertWWriterAvailable('global/Firebase/products.ts#batchUpdateProducts')
  reportFirestoreOnlyWriterUsage('batchUpdateProducts')
  const batch = rootDb.batch()

  for (const product of products) {
    const productRef = getFirestoreRef(product, 'products')
    batch.update(productRef, product)
  }

  await batch.commit()
}

// ============================================================================
// storeStock → Supabase mirror (ATL-762)
// ============================================================================

/**
 * ATL-762 — `updateProduct` commits an ABSOLUTE `storeStock` overwrite to Firestore
 * (`batch.update` below) while `EXCLUDED_FIELDS` strips `storeStock` out of the
 * `products.update` DTO, and NOTHING called the dedicated stock endpoint. A CSV stock import
 * (`ExportImportScreen.js:346` → `updateProduct`) therefore moved Firestore and left Supabase
 * untouched — in bulk, thousands of units per run — and `bestEffortDualWrite` guarantees the loss
 * is silent. This block is the mirror.
 *
 * Semantics that must not be re-derived incorrectly: `PATCH /products/:id/stock` takes the
 * ABSOLUTE `newStock`; the backend hands it to the `set_stock_with_movement` RPC as
 * `new_quantity` and the RPC derives the movement from the Supabase LEDGER (ATL-729). So the
 * frontend must NOT compute a delta — send the same absolute value Firestore just received. Same
 * endpoint and same `products.updateStock` label as `saveNewProduct`'s initial-stock loop, so the
 * existing Sentry dashboards keep grouping.
 */

/** Why a store present in the `storeStock` payload was NOT mirrored to Supabase. */
type StockMirrorSkipReason =
  /** The absolute value equals the store's stock before this write — no stock event happened. */
  | 'unchanged'
  /** The value is not a finite number. Sending `0` instead would ZERO a customer's stock. */
  | 'uncoercible-value'
  /** The key is not a known store id. Never invent/derive one (the ATL-706/714 class). */
  | 'unknown-store'

interface StockMirrorPlan {
  updates: Array<{ storeId: string; newStock: number }>
  skipped: Array<{ storeId: string; reason: StockMirrorSkipReason; rawValue: unknown }>
}

/**
 * Decide, per store, whether the absolute `storeStock` value must be mirrored to Supabase.
 *
 * Pure and exported for unit testing. `currentStoreStock === null` means "current stock unknown",
 * which deliberately mirrors EVERYTHING: an unknown current value must never become a reason to
 * skip, because skipping is what ATL-762 already was. Likewise `knownStoreIds === null` means
 * "the store list is unavailable, so nothing can be judged unknown" — the guard can only ever
 * reject a store it can positively prove is not in the org's store list.
 */
export function planStoreStockMirror(
  storeStock: Record<string, unknown>,
  currentStoreStock: Record<string, unknown> | null,
  knownStoreIds: ReadonlySet<string> | null,
): StockMirrorPlan {
  const plan: StockMirrorPlan = { updates: [], skipped: [] }

  for (const [storeId, rawValue] of Object.entries(storeStock)) {
    if (knownStoreIds && !knownStoreIds.has(storeId)) {
      plan.skipped.push({ storeId, reason: 'unknown-store', rawValue })
      continue
    }

    // Comma-aware coercion (ATL-633 precedent): the product form seeds its stock inputs with
    // `transformNumbersToString(...)`, so an edit can submit "42" / "1,5".
    //
    // Nothing downstream will catch that for us — this is the ONLY coercion on the path.
    // `PATCH /products/:id/stock` takes an INLINE body type (products.controller.ts:137), not a
    // class-validator DTO, so there is no @IsNumber() and no 400; and the api-client sends it with
    // no request schema, so no client-side Zod rejection either. A string therefore travels all the
    // way into `set_stock_with_movement`, where `jsonb_to_recordset(... new_quantity NUMERIC)`
    // either coerces it or raises a cast error — and `bestEffortDualWrite` would swallow the error.
    //
    // `null` (unparseable) must NOT become 0 — that would zero real stock, so skip + report.
    const newStock = parseDecimalValue(rawValue)
    if (newStock === null) {
      plan.skipped.push({ storeId, reason: 'uncoercible-value', rawValue })
      continue
    }

    // Skip stores whose absolute value did not move. The product form submits EVERY store's
    // stock even when one changed, and re-sending an unchanged absolute is not free: it writes a
    // zero-delta movement per store per edit into the Supabase ledger. Only a value that
    // provably equals the pre-write stock is skipped; anything unknown is mirrored.
    //
    // ⚠️ REMEDIATION TRAP — what this skip deliberately CANNOT do, and what support must not be
    // told. For an org already damaged by ATL-762 (Firestore 25, Supabase 10), RE-IMPORTING THE
    // SAME SHEET IS A SILENT NO-OP: the dict says 25, the sheet says 25, so it is "unchanged",
    // Supabase stays at 10, and — because `unchanged` is routine — no Sentry event is emitted
    // either. So "just re-import your file" does NOT heal pre-fix damage. Only the ATL-486
    // re-migration campaign does (detected by the absolute stock comparison in
    // apps/atlas-backend/scripts/check-stock-fidelity.ts, never by an id-level diff).
    //
    // This is the correct trade anyway: the alternative — mirroring every unchanged store — would
    // write a zero-delta movement per store on every product edit fleet-wide to opportunistically
    // paper over historic drift, i.e. hide a known data problem behind ledger noise instead of
    // remediating it. Do not "fix" this skip to chase a backfill.
    const currentStock = currentStoreStock ? parseDecimalValue(currentStoreStock[storeId]) : null
    if (currentStock !== null && currentStock === newStock) {
      plan.skipped.push({ storeId, reason: 'unchanged', rawValue })
      continue
    }

    plan.updates.push({ storeId, newStock })
  }

  return plan
}

/**
 * The product's per-store stock BEFORE this write, snapshotted (never a live reference).
 *
 * Read from the products dict — the app's live view of the active read source, i.e. the same
 * store of record whose absolute value we are about to overwrite — falling back to the passed
 * product object, then to `null` (unknown ⇒ mirror everything).
 *
 * MUST be called before `batch.commit()`: after the commit, the products subscription writes our
 * own new value back into the dict, and comparing against that would make every store look
 * "unchanged" and silently re-open ATL-762.
 */
function snapshotCurrentStoreStock(
  refOrItem: MockRef | ItemWithRef,
  allFlatProductsDict: Record<string, Product> | undefined,
  productId: string,
): Record<string, unknown> | null {
  const fromDict = allFlatProductsDict?.[productId]?.storeStock
  if (fromDict && typeof fromDict === 'object') return { ...fromDict }
  const fromItem = (refOrItem as { storeStock?: unknown } | undefined)?.storeStock
  if (fromItem && typeof fromItem === 'object') return { ...(fromItem as Record<string, unknown>) }
  return null
}

/**
 * The org's known store ids, or `null` when the stores atom cannot be read (cold start, or a
 * non-app runtime). `null` disables the unknown-store guard entirely — an unverifiable store list
 * must never silently stop stock from being mirrored.
 */
/**
 * The read-model value of this product — the object the caller's payload was composed FROM.
 *
 * ATL-1041: deliberately the products dict ONLY, never `refOrItem`. The dict IS the read model
 * every edit form seeds from (`ProductDetailScreen` reads `productsDict[id] || productItemProp`),
 * whereas `refOrItem` can be a bare Firestore ref, or an entity that merely embeds a product-shaped
 * snapshot — `SupplyDetailScreen` passes a SUPPLY LINE here. A wrong baseline is worse than no
 * baseline: it can make a real edit look unchanged and silently drop it. Absent ⇒ `null` ⇒
 * `buildWriteBackDelta` passes the whole payload through, i.e. exactly today's behaviour.
 *
 * Sanitized with the same function as the payload so the two are compared in the same space:
 * Timestamps become Dates on both sides, and the live cyclic `ref` (ATL-554) is dropped.
 */
function snapshotReadModelProduct(
  allFlatProductsDict: Record<string, Product> | undefined,
  productId: string,
): Record<string, unknown> | null {
  const fromDict = allFlatProductsDict?.[productId]
  if (!fromDict || typeof fromDict !== 'object') return null
  return sanitizeForFirestore(fromDict) as Record<string, unknown>
}

/**
 * Change-provenance metadata: it describes THIS write, not the entity's state, so "unchanged"
 * is meaningless for it and it is always written.
 */
const PRODUCT_WRITE_METADATA = new Set([
  'lastChangeType',
  'lastChangeBy',
  'lastChangeAt',
  'lastChangeSource',
  'lastChangeStore',
  'organizationId',
  'dailyStatID',
])

/**
 * A map whose keys are not legal Firestore field-path segments cannot be written leaf-wise, so the
 * delta falls back to replacing the whole map — i.e. to the unguarded behaviour this module exists
 * to remove. That must not be silent: it is the one path on which a product update can still
 * delete a Firestore key it never read.
 */
function reportUnsafeWriteBackKeys(productId: string, fields: readonly string[]): void {
  if (fields.length === 0) return
  Sentry.captureMessage(`[ATL-1041] write-back fell back to a whole-map write: ${fields.join(', ')}`, {
    level: 'warning',
    tags: { dual_write_module: 'products', write_back_fallback: 'unsafe-field-path-key' },
    fingerprint: ['write-back-unsafe-key', ...fields],
    extra: { productId, fields },
  })
}

function getKnownStoreIds(): ReadonlySet<string> | null {
  try {
    const storesDict = getDefaultStore().get(storesDictAtom) as Record<string, unknown> | undefined
    const ids = storesDict ? Object.keys(storesDict) : []
    return ids.length > 0 ? new Set(ids) : null
  } catch {
    return null
  }
}

/**
 * Report the stores whose stock was NOT mirrored for a reason that is a DEFECT in the data
 * (unparseable value / unknown store id), not routine (`unchanged`).
 *
 * These skips are the honest alternative to `newStock: value || 0`: they leave Firestore and
 * Supabase out of sync for that one store rather than writing a wrong number, so they have to be
 * visible. Fingerprinted per reason so each class is its own alertable issue.
 */
function reportSkippedStockMirror(
  productId: string,
  skipped: StockMirrorPlan['skipped'],
): void {
  for (const { storeId, reason, rawValue } of skipped) {
    if (reason === 'unchanged') continue
    const detail = {
      productId,
      storeId,
      reason,
      // Stock quantity, not PII; truncated so a pathological value can't bloat the event.
      rawValue: String(rawValue).slice(0, 32),
      rawValueType: typeof rawValue,
    }
    // eslint-disable-next-line no-console
    console.warn(
      `[dualWrite] products.updateStock SKIPPED for store ${storeId} (${reason}) — Firestore stock ` +
        `changed but Supabase was not updated for this store`,
      JSON.stringify(detail),
    )
    Sentry.captureMessage(`storeStock mirror skipped: ${reason}`, {
      level: 'warning',
      tags: {
        supabase_dual_write_error: true,
        dual_write: 'products.updateStock',
        dual_write_module: 'products',
        dual_write_op: 'updateStock',
        stock_mirror_skip: reason,
      },
      fingerprint: ['stock-mirror-skipped', reason],
      contexts: { stock_mirror: detail },
    })
  }
}

/**
 * Mirror an absolute `storeStock` map to Supabase in ONE bulk call for the product (ATL-765),
 * via the shared `sendBulkStockMirror` (stockMirror.ts) — which folds queued failed writes in
 * and re-queues per-item failures. A dedicated stock call (rather than letting the
 * `products.update` DTO carry `storeStock`) is deliberate: per-store failure isolation, skipped
 * unchanged stores, and the store id visible in the payload for parity checks.
 */
async function mirrorStoreStockToSupabase(
  productId: string,
  storeStock: Record<string, unknown>,
  currentStoreStock: Record<string, unknown> | null,
): Promise<void> {
  const { updates, skipped } = planStoreStockMirror(storeStock, currentStoreStock, getKnownStoreIds())

  reportSkippedStockMirror(productId, skipped)

  await sendBulkStockMirror(productId, updates, storeStock)
}

// ============================================================================
// Update Operations
// ============================================================================

/**
 * Update an existing product
 */
export const updateProduct = async (
  db: unknown,
  refOrItem: MockRef | ItemWithRef,
  updatedInfo: Record<string, unknown>,
  allFlatProductsDict: Record<string, Product>
): Promise<void> => {
  assertWWriterAvailable('global/Firebase/products.ts#updateProduct')
  const batch = rootDb.batch()
  // Accepts the whole product object (preferred — works even when `.ref` was stripped;
  // QARASAY-KASSA-26H) or a ref/mock ref. Id comes from the resolved ref, not `.ref.id`.
  const firestoreRef = getFirestoreRef(refOrItem, 'products')
  const productId: string = firestoreRef.id

  // ATL-762: snapshot the pre-write per-store stock BEFORE anything is committed. Reading it
  // after batch.commit() would race the products subscription writing our own new value back
  // into the dict, making every store look "unchanged" and silently re-opening the bug.
  const currentStoreStock = snapshotCurrentStoreStock(refOrItem, allFlatProductsDict, productId)

  // Sanitize data for Firestore - converts mock Timestamps to Dates
  const sanitizedInfo = sanitizeForFirestore(updatedInfo) as Record<string, unknown>

  // ATL-1041: `updatedInfo` is a form seeded from the read model, not a set of edits. Every caller
  // composes it that way — `ProductDetailScreen` seeds all 27 fields from `productsDictAtom` and
  // `validateProduct`'s update branch copies every key straight through. While reads come from
  // Firestore that is merely wasteful; after an org's read cutover the dict holds SUPABASE's
  // rendering of the product, and writing it back replaces the Firestore document — the rollback
  // copy nothing in this flow has read — with whatever the read transform managed to reconstruct.
  // Measured casualties: `salePrices.<store>_priceType_<n>` keys the transform cannot build
  // (ATL-1012), `productImages` reduced to `[{url}]` when `image_url IS NULL` (ATL-911),
  // `ingredients` reduced to the `{id,name,quantity}` projection, `productTags` reduced to `[]`
  // because Supabase has no column for it at all. ATL-1012 records this already happening once:
  // invented `_retail` keys were laundered into Firestore by exactly this round trip and are
  // still there.
  //
  // So write the DELTA against the read model, addressing changed map leaves by field path. Keys
  // the transform never showed us are then neither overwritten nor deleted, which is the only
  // property that holds against a Firestore document we have not read.
  //
  // NOT the same thing as `EXCLUDED_FIELDS` below: that filters the SUPABASE DTO, is built after
  // `batch.commit()`, and has never guarded this write.
  const productDelta = buildWriteBackDelta(sanitizedInfo, snapshotReadModelProduct(allFlatProductsDict, productId), {
    alwaysWrite: PRODUCT_WRITE_METADATA,
  })
  reportUnsafeWriteBackKeys(productId, productDelta.unsafeKeyFields)

  // Firestore rejects an empty `update()`; an empty delta means the payload asserted nothing the
  // read model did not already hold, so there is nothing to write.
  if (Object.keys(productDelta.update).length > 0) {
    batch.update(firestoreRef, productDelta.update)
  }
  // The `history` subcollection keeps the FULL submitted payload, deliberately: it is a new,
  // append-only audit document, so it cannot overwrite anything, and a delta would make the audit
  // trail less informative than it is today.
  // Firestore refs have subcollection access
  const refWithCollection = firestoreRef as unknown as { collection: (name: string) => { doc: () => MockRef } }
  batch.set(refWithCollection.collection('history').doc(), sanitizedInfo)

  // Check other products for ingredients - update composite products containing this product
  const compositeProductsContainingThisProduct: Array<Product & { id: string }> = []
  for (const [, productEntry] of Object.entries(allFlatProductsDict)) {
    const product = productEntry as Product & { id: string }
    if (product.ingredients && product.ingredients.length > 0) {
      const ingrIndex = product.ingredients.findIndex((item) => item.id === productId)
      if (ingrIndex !== -1) {
        // Part of some other composite product
        const updatedProductIngredients = [...product.ingredients]
        updatedProductIngredients[ingrIndex] = { ...product.ingredients[ingrIndex], ...updatedInfo }

        // Create a temporary products dict with the updated product info
        const tempProductsDict = { ...allFlatProductsDict }
        tempProductsDict[productId] = { ...allFlatProductsDict[productId], ...updatedInfo } as Product

        // Calculate purchase price using the temporary products dict
        let updatedPurchasePrice = 0
        for (const ingredient of updatedProductIngredients) {
          const ingredientProduct = tempProductsDict[ingredient.id]
          if (ingredientProduct) {
            let ingredientPrice = 0
            if (ingredientProduct.isComposite && ingredientProduct.ingredients && ingredientProduct.ingredients.length > 0) {
              // For composite ingredients, calculate from their ingredients
              for (const subIngredient of ingredientProduct.ingredients) {
                const subProduct = tempProductsDict[subIngredient.id]
                if (subProduct) {
                  ingredientPrice += ((subProduct.purchasePrice || 0) + (subProduct.packagePrice || 0)) * subIngredient.quantity
                }
              }
            } else {
              // For regular ingredients, use purchasePrice + packagePrice
              ingredientPrice = (ingredientProduct.purchasePrice || 0) + (ingredientProduct.packagePrice || 0)
            }
            updatedPurchasePrice += ingredientPrice * ingredient.quantity
          }
        }
        const updatedProduct = { ...product, purchasePrice: updatedPurchasePrice, ingredients: updatedProductIngredients }
        compositeProductsContainingThisProduct.push(updatedProduct)
      }
    }
  }

  if (compositeProductsContainingThisProduct.length > 0) {
    for (const product of compositeProductsContainingThisProduct) {
      if (product.id !== productId) {
        const productFirestoreRef = getFirestoreRef(product, 'products')
        const sanitizedProduct = sanitizeForFirestore(product) as Record<string, unknown>
        // ATL-1041: this is the largest instance of the read-back-write class in the app, and the
        // only one with NO user in the loop. `product` is a read-model object taken straight from
        // `allFlatProductsDict`; the cascade changes exactly two things on it (`purchasePrice` and
        // the ingredient entry) and then wrote the WHOLE object back — up to 158 composite
        // documents rewritten from Supabase's rendering because someone edited one ingredient's
        // price (p90 12, p99 49; see the ATL-950 note at the bulk cascade below).
        //
        // No `alwaysWrite`: unlike the edited product, the cascade deliberately asserts no change
        // metadata — leaving each composite's own `lastChangeAt` untouched is what ATL-950 relies
        // on, and re-asserting identical metadata would be noise, not provenance.
        const cascadeDelta = buildWriteBackDelta(
          sanitizedProduct,
          snapshotReadModelProduct(allFlatProductsDict, product.id),
        )
        reportUnsafeWriteBackKeys(product.id, cascadeDelta.unsafeKeyFields)
        if (Object.keys(cascadeDelta.update).length > 0) {
          batch.update(productFirestoreRef, cascadeDelta.update)
        }
        const productRefWithCollection = productFirestoreRef as unknown as { collection: (name: string) => { doc: () => MockRef } }
        batch.set(productRefWithCollection.collection('history').doc(), sanitizedProduct)
      }
    }
  }

  // Update daily stats
  const statFields = {
    productsUpdated: fieldValue.increment(1),
  }
  const dbRef = db as unknown as { collection: (name: string) => { doc: (id: string) => unknown } }
  batch.set(
    dbRef.collection('dailyStats').doc(updatedInfo.dailyStatID as string),
    {
      ...statFields,
      store: updatedInfo.lastChangeStore,
      date: updatedInfo.lastChangeAt,
      organizationId: updatedInfo.organizationId,
    },
    { merge: true }
  )

  await batch.commit()

  // Dual write to Supabase via NestJS backend
  // Per data-transformation architecture Principle #6: Frontend Pass-Through
  // Pass data directly to backend API — no field-by-field mapping
  // Exclude only fields handled separately or not relevant to backend
  const EXCLUDED_FIELDS = new Set([
    // Stock updates go through the dedicated stock endpoint — mirrored per store by
    // mirrorStoreStockToSupabase() below (ATL-762). Keep it excluded here so the change is applied
    // exactly ONCE: the backend WOULD also apply `storeStock` if it arrived in this DTO
    // (products.service.ts:930-960), which would double-write the same absolute value.
    'storeStock',
    'createdAt',        // Read-only timestamp
    'imageUploading',   // UI state, not a product field
  ])
  const updateDto: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(updatedInfo)) {
    if (!EXCLUDED_FIELDS.has(key) && value !== undefined) {
      updateDto[key] = value
    }
  }
  // Strip extra fields from ingredients (only id, name, quantity needed by backend)
  if (updateDto.ingredients) {
    updateDto.ingredients = (updateDto.ingredients as ProductIngredient[]).map(ing => ({
      id: ing.id,
      name: ing.name,
      // Coerce comma-aware so the @IsNumber() backend DTO matches the Firestore value even when
      // the editor leaves a comma-decimal string (ATL-633): "1,5" → 1.5, not 1 or 0.
      quantity: ingredientQuantityToNumber(ing.quantity),
    }))
  }

  const updatedProduct = await bestEffortDualWrite('products.update', () => productsApi.updateProduct(productId, updateDto))

  // ATL-950: the composite cost cascade above wrote recomputed `purchasePrice` values for every
  // composite containing this product into the SAME Firestore batch — and, until this call, into
  // Firestore ONLY. The dual-write just above posts the edited product and nothing else, so every
  // cascade was a one-sided write. It stayed invisible because the cascade deliberately leaves each
  // composite's own `lastChangeAt` untouched: both stores agree on WHEN the composite changed and
  // disagree on WHAT it costs, so no freshness check can see it. Measured 2026-08-24: 116 of 143
  // genuine `purchasePrice` drifts.
  //
  // ONE bulk call, never one per composite — the fan-out reaches 158 composites for a single edit
  // in production (p90 12, p99 49), and that many sequential round trips from a till on mobile data
  // is not a write path.
  //
  // The cost is COPIED from what Firestore just stored, never recomputed here or server-side: two
  // independent opinions of what a composite costs is the defect this repairs, not the fix.
  //
  // NOT queued on failure, deliberately. `bestEffortDualWrite` queues transient failures in the
  // persisted outbox keyed by (label, firestoreId), and a replayed cascade would write an ABSOLUTE
  // cost computed from an older ingredient graph — able to overwrite a newer correct value, with no
  // version field on the composite to make the replay safe (the untouched `lastChangeAt` is the
  // very mechanism that hides this class). So a failed cascade is reported and left for the
  // ATL-950 backfill, which is strictly better than today, where EVERY cascade is lost.
  if (compositeProductsContainingThisProduct.length > 0) {
    const cascadeUpdates = compositeProductsContainingThisProduct
      .filter((composite) => composite.id !== productId)
      .map((composite) => ({
        productId: composite.id,
        purchasePrice: composite.purchasePrice ?? 0,
        // Compacted the same way the edited product's own ingredients are above: the cascade
        // merged the WHOLE edited product into the ingredient entry, and the backend contract is
        // the `{id,name,quantity}` snapshot.
        ingredients: (composite.ingredients ?? []).map((ing) => ({
          id: ing.id,
          name: ing.name,
          quantity: ingredientQuantityToNumber(ing.quantity),
        })),
      }))
    if (cascadeUpdates.length > 0) {
      const cascadeResponse = await bestEffortDualWrite('products.costCascade', () =>
        productsApi.updateCostCascadeBulk({ updates: cascadeUpdates, triggeredBy: productId }),
      )
      // The endpoint answers 200 with per-item results, so a rejected composite would otherwise
      // pass as a landed write. Report each one — an unstorable cost is the ATL-939 money-column
      // class and must stay visible.
      for (const result of cascadeResponse?.results ?? []) {
        if (result.ok) continue
        Sentry.captureMessage(`[ATL-950] cost cascade rejected for ${result.productId}`, {
          level: 'warning',
          tags: { supabase_dual_write_error: true, dual_write: 'products.costCascade' },
          extra: { productId: result.productId, error: result.error, triggeredBy: productId },
        })
      }
    }
  }

  // ATL-762: mirror the absolute storeStock overwrite Firestore just took. Runs regardless of the
  // products.update result — the two are independent (the stock endpoint resolves the product by
  // its Firestore id), and a rejected metadata field must not also drop the stock change.
  const submittedStoreStock = updatedInfo.storeStock
  if (submittedStoreStock && typeof submittedStoreStock === 'object' && !Array.isArray(submittedStoreStock)) {
    await mirrorStoreStockToSupabase(
      productId,
      submittedStoreStock as Record<string, unknown>,
      currentStoreStock,
    )
  }

  // Update local state immediately after successful save
  // This ensures the products atom has the new values before real-time subscription fires
  // (Real-time only sends main table data, not related tables like prices/activeInStore)
  if (updatedProduct) {
    // ATL-1445 stage 3 P6: the SERVER atom, read and written (see `saveNewProduct`).
    const jotaiStore = getDefaultStore()
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- productsDictAtom has complex union type
    const currentProductsDict = jotaiStore.get(serverProductsDictAtom) as Record<string, any>

    // Use firestoreId from API response as the key (matches how products are loaded)
    const productKey = updatedProduct.firestoreId || productId
    const existingProduct = currentProductsDict[productKey]

    // Merge the updated values into the existing product
    // Use the updateDto values which reflect what was actually saved
    const mergedProduct = {
      ...existingProduct,
      ...updatedInfo,
      // Ensure store-keyed fields are properly merged
      salePrices: updateDto.salePrices || existingProduct?.salePrices || {},
      activeInStore: updateDto.activeInStore || existingProduct?.activeInStore || {},
    }

    jotaiStore.set(serverProductsDictAtom, {
      ...currentProductsDict,
      [productKey]: mergedProduct,
    })

    // Trigger ProductList re-render by incrementing updateCount
    const currentUpdated = jotaiStore.get(productsUpdatedAtom)
    jotaiStore.set(productsUpdatedAtom, {
      ...currentUpdated,
      updateCount: currentUpdated.updateCount + 1,
      lastLocalUpdate: new Date(),
    })
  }

  // Note: Product history is tracked automatically by the backend via stored procedures
}
